/**
 * Unified retry policy for stream connections.
 *
 * Replaces the project's mix of exponential, linear, and fixed backoff
 * implementations with a single configurable policy.
 */

import { ERetryStrategy, type IRetryConfig, type IRetryInfo } from './types';

/** Default retry configuration. */
export const DEFAULT_RETRY_CONFIG: IRetryConfig = {
  maxAttempts: 5,
  strategy: ERetryStrategy.EXPONENTIAL,
  baseDelayMs: 1_000,
  maxDelayMs: 30_000,
};

/**
 * Computes the delay for a given retry attempt.
 *
 * @param attempt - 1-based attempt number
 * @param config - Retry configuration
 * @returns Delay in milliseconds before the next attempt
 */
export function computeRetryDelay(
  attempt: number,
  config: IRetryConfig,
): number {
  const boundedAttempt = Math.max(1, attempt);
  const maxDelay = config.maxDelayMs ?? 30_000;
  let delay: number;

  switch (config.strategy) {
    case ERetryStrategy.EXPONENTIAL:
      delay = config.baseDelayMs * 2 ** (boundedAttempt - 1);
      break;
    case ERetryStrategy.LINEAR:
      delay = config.baseDelayMs * boundedAttempt;
      break;
    case ERetryStrategy.FIXED:
      delay = config.baseDelayMs;
      break;
  }

  return Math.min(delay, maxDelay);
}

/**
 * Determines whether another retry attempt should be made.
 *
 * @param attempt - Current 1-based attempt number
 * @param config - Retry configuration
 * @returns True if more attempts remain
 */
export function shouldRetry(attempt: number, config: IRetryConfig): boolean {
  return attempt < config.maxAttempts;
}

/**
 * Builds an {@link IRetryInfo} object for callbacks.
 *
 * @param attempt - 1-based attempt number
 * @param config - Retry configuration
 * @param reason - Why the retry was triggered
 * @returns Retry info for consumer callbacks
 */
export function buildRetryInfo(
  attempt: number,
  config: IRetryConfig,
  reason: string,
): IRetryInfo {
  return {
    attempt,
    maxAttempts: config.maxAttempts,
    delayMs: computeRetryDelay(attempt, config),
    reason,
  };
}

/**
 * Merges partial user configuration with defaults.
 *
 * @param partial - User-provided overrides
 * @returns Complete retry configuration
 */
export function resolveRetryConfig(
  partial?: Partial<IRetryConfig>,
): IRetryConfig {
  return {
    ...DEFAULT_RETRY_CONFIG,
    ...partial,
  };
}

/**
 * Returns a promise that resolves after a delay, or immediately if the signal is aborted.
 *
 * @param delayMs - Delay in milliseconds
 * @param signal - Optional AbortSignal to cancel the wait
 * @returns Promise that resolves when the delay expires or signal aborts
 */
export function waitWithAbort(
  delayMs: number,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise<void>(resolve => {
    if (signal?.aborted) {
      resolve();
      return;
    }

    const timer = setTimeout(resolve, delayMs);

    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}
